import { useEffect, useRef, useCallback } from 'react';
import { useAuthContext } from './useAuthContext';
import { BVTConstants } from '../utils/BVTConstants';
import { sessionUtils } from '../utils/sessionUtils';
import { useAuthHeader } from './useAuthHeader';
import toast from 'react-hot-toast';
import axios from '../services/ApiService';

export const useActivityTracker = () => {
  const { dispatch } = useAuthContext();
  const { setAuthHeader } = useAuthHeader();
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const heartbeatRef = useRef(null);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      // User has been inactive for the configured timeout period
      toast.error('Session expired due to inactivity. Please login again.');
      dispatch({ type: 'LOGOUT' });
      window.location.href = '/login';
    }, BVTConstants.SESSION_INACTIVITY_TIMEOUT);
  }, [dispatch]);

  // Heartbeat function to refresh JWT token
  const sendHeartbeat = useCallback(async () => {
    try {
      const response = await axios.post('/heartbeat');
      if (response.data && response.data.token) {
        setAuthHeader(response.data.token);
      }
    } catch (error) {
      console.error('[Activity Tracker] Failed to refresh JWT token:', error);
    }
  }, []);

  const handleUserActivity = useCallback(() => {
    resetTimer();
    const timeUntilLogout = BVTConstants.SESSION_INACTIVITY_TIMEOUT;
    sessionUtils.logSessionActivity('User activity detected', timeUntilLogout);
  }, [resetTimer]);

  useEffect(() => {
    // Events to track for user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'focus'
    ];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Page became visible, reset timer
        resetTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initialize timer
    resetTimer();

    // Start heartbeat to refresh JWT token every 45 minutes (before 1-hour expiry)
    // This ensures the token is refreshed while the user is active
    heartbeatRef.current = setInterval(() => {
      sendHeartbeat();
    }, BVTConstants.HEARTBEAT_INTERVAL); // 45 minutes

    // Send initial heartbeat after 5 minutes to ensure token is fresh
    setTimeout(() => {
      sendHeartbeat();
    }, BVTConstants.HEARTBEAT_INITIAL_DELAY); // 5 minutes

    // Cleanup function
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
      
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
    };
  }, [handleUserActivity, resetTimer, sendHeartbeat]);

  // Return functions that can be used to manually reset the timer
  return {
    resetTimer,
    getLastActivity: () => lastActivityRef.current,
    getTimeUntilLogout: () => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      return Math.max(0, BVTConstants.SESSION_INACTIVITY_TIMEOUT - timeSinceLastActivity);
    },
    sendHeartbeat // Expose heartbeat function for manual calls
  };
}; 